import { ref } from "vue";
import type { Ref } from "vue";
import { useIdleReady } from "@mkbabb/glass-ui/dom";
import type { IPInfo, LookedUpIP } from "@src/types/dns";
import { getClientIp, getIpInfo, lookupIp } from "@src/api/ip.ts";

/**
 * Composable that manages client IP address fetching and IP metadata lookups.
 *
 * On creation, automatically fetches the client's public IP, ISP/org info,
 * and entity lookup data from the Friday Institute API endpoints.
 */
export function useIPInfo() {
    /** The client's public IP address. */
    const clientIp: Ref<string> = ref("");

    /** ISP and geographic metadata for the client IP. */
    const ipInfo: Ref<IPInfo | null> = ref(null);

    /** Entity/organization lookup result for the client IP. */
    const lookedUpIp: Ref<LookedUpIP | null> = ref(null);

    /** Whether any of the IP info requests are currently in-flight. */
    const isLoading: Ref<boolean> = ref(false);

    /**
     * Fetch the client's public IP address. Delegates to
     * `api/ip.ts#getClientIp`, which owns the Friday-Institute
     * primary + ipify fallback + 3s AbortController timeout shape.
     */
    async function getIP(): Promise<string> {
        return getClientIp();
    }

    /**
     * Fetch ISP/organization metadata for the given IP address.
     * Falls back to fetching the client IP first if none is provided.
     */
    async function getIPInfo(ip?: string): Promise<IPInfo> {
        const resolved = ip ?? (await getIP());
        return getIpInfo(resolved);
    }

    async function lookupIP(ip?: string): Promise<LookedUpIP> {
        const resolved = ip ?? (await getIP());
        return lookupIp(resolved);
    }

    /**
     * Refresh all IP-related data: fetch client IP, then ISP info and entity lookup
     * in parallel. Sets isLoading during the entire operation.
     */
    async function refresh(): Promise<void> {
        isLoading.value = true;

        try {
            const ip = await getIP();
            clientIp.value = ip;

            // Fetch IP info and lookup in parallel since they are independent.
            const results = await Promise.allSettled([
                getIPInfo(ip),
                lookupIP(ip),
            ]);

            if (results[0].status === "fulfilled") {
                ipInfo.value = results[0].value;
            } else {
                console.error("Failed to fetch IP info:", results[0].reason);
            }

            if (results[1].status === "fulfilled") {
                lookedUpIp.value = results[1].value;
            } else {
                console.error("Failed to lookup IP:", results[1].reason);
            }
        } catch (error) {
            console.error("Failed to fetch client IP:", error);
        } finally {
            isLoading.value = false;
        }
    }

    // Defer the auto-fetch to first idle so the three network calls
    // don't fire immediately at instantiation, contending with the
    // eager bundle parse + speedtest auto-start for TCP slots.
    //
    // R0G-3: `useIdleReady` (glass-ui /dom) is the promoted scope-aware idle
    // gate — the `onReady` task runs once on the first idle tick and is
    // auto-cancelled on scope dispose, so a short-lived consumer cannot leak
    // a deferred `refresh()` that writes to disposed refs. The composable owns
    // the rIC + Safari `setTimeout(0)` fallback internally (FA-2 §3.3 cleanup
    // moves to the primitive).
    useIdleReady({ timeout: 2000, onReady: () => void refresh() });

    return {
        /** The client's public IP address. */
        clientIp,
        /** ISP and geographic metadata for the client IP. */
        ipInfo,
        /** Entity/organization lookup result for the client IP. */
        lookedUpIp,
        /** Whether any IP info requests are currently in-flight. */
        isLoading,
        /** Re-fetch all IP information from scratch. */
        refresh,
    };
}
